import { SearchComponent } from "@/App/Tenants/Search";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { TableData } from "@/App/Distributions/Table";
import { getContestssData, searchContestsData } from "@/Api/contests";
import { getSelectProductGroups } from "@/Api/productGroups";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
export type TableDataProps = {
  id: number;
  uuid: string;
  name: string;
  validation_date: string;
  lottery_id: number;
  prize: number;
  is_active: boolean;
  contest_status_id: number;
  tenant_id: number;
  has_investigation: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  contest_status: {
    id: number;
    name: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  tenant: {
    id: number;
    name: string;
  };
  product_classifications: {
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    lottery: {
      id: number;
      name: string;
    };
    product_classification: {
      id: number;
      uuid: string;
      lottery_id: number;
      name: string;
      game_quantity: number;
      dez_quantity: number;
      quota_quantity: number;
      total_without_tariff: number;
      total_with_tariff: number;
      game_percentage: number;
      tariff: number;
      sale_price: number;
      price_without_tariff: number;
      commission_by_quote: number;
      image: string | null;
      is_active: number;
      product_type_id: number;
      tenant_id: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    };
  }[];
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<string[]>([]);
  const [validationStartDate, setValidationStartDate] = useState<string>("");
  const [validationEndDate, setValidationEndDate] = useState<string>("");

  const fetchData = async (
    page: number = 1,
    lotteryIds: string[] = [],
    startDate: string = "",
    endDate: string = ""
  ) => {
    setState("loading");
    const { data } = await getContestssData(page, {
      contest_status_id: 1,
      is_active: 1,
      lottery_id: lotteryIds.length > 0 ? lotteryIds : undefined,
      validation_start_date: startDate || undefined,
      validation_end_date: endDate || undefined,
    });
    setListData(data?.response);
    setState("production");
  };

  useEffect(() => {
    fetchData();
    (async () => {
      const { data, ok } = await getSelectProductGroups();
      if (ok && data?.response) {
        setLotteries(data.response);
      }
    })();
  }, []);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(
          listData?.current_page + 1,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(
          listData?.current_page - 1,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(
          listData?.last_page,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
  };

  const handleApplyFilter = () => {
    fetchData(1, selectedLotteries, validationStartDate, validationEndDate);
  };

  const handleClearFilter = () => {
    setSelectedLotteries([]);
    setValidationStartDate("");
    setValidationEndDate("");
    fetchData(1, [], "", "");
  };

  const hasActiveFilters =
    selectedLotteries.length > 0 || !!validationStartDate || !!validationEndDate;

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-semibold flex gap-2 items-center">
          Distribuição
        </h2>
      </div>
      <div className="flex flex-col gap-3">
        <SearchComponent
          updateFront={setListData}
          setState={setState}
          funcSearch={searchContestsData}
          placeholder="Pesquisar concursos"
        />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filtros</CardTitle>
              <div className="flex gap-2">
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={handleClearFilter} className="h-8">
                    <X className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                  className="h-8"
                >
                  {isFiltersExpanded ? "Ocultar" : "Expandir"}
                </Button>
              </div>
            </div>
          </CardHeader>

          {isFiltersExpanded && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="lottery-filter">Loteria</Label>
                  <MultiSelectCheckbox
                    options={lotteries.map((lottery) => ({
                      value: lottery.id.toString(),
                      label: lottery.name,
                    }))}
                    selectedValues={selectedLotteries}
                    onSelectionChange={setSelectedLotteries}
                    placeholder="Selecione a loteria"
                    selectAllLabel="SELECIONAR TUDO"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="validation_start_date">Validade Início</Label>
                  <Input
                    id="validation_start_date"
                    type="date"
                    value={validationStartDate}
                    onChange={(e) => setValidationStartDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="validation_end_date">Validade Fim</Label>
                  <Input
                    id="validation_end_date"
                    type="date"
                    value={validationEndDate}
                    onChange={(e) => setValidationEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleApplyFilter} className={cn(colors.bg_primary, "gap-2")}>
                  <Search className="w-4 h-4" />
                  Aplicar Filtros
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {state === "loading" ? (
          <CreateLoaderTable cols={4} lines={6} />
        ) : (
          <TableData
            updateFront={setListData}
            dataApi={listData}
            pageActions={pageActions}
          />
        )}
      </div>
    </Main>
  );
};
